/**
 * Official Hong Kong design values.
 *
 * EVERY number in this file is traceable to a published government document.
 * Nothing here is tuned by feel. If a value needs to change, change the
 * citation with it - the whole premise of this project is that the driving
 * layer is built to spec rather than eyeballed, and this file is where that
 * promise is kept.
 *
 * Sources
 *   TPDM  Transport Planning and Design Manual (Transport Department, 03/2026)
 *         https://www.td.gov.hk/filemanager/en/content_5055/V{2,3,4}_03_2026.pdf
 *   HyD   Highways Department Standard Drawings
 *
 * Units are SI: metres, seconds, radians, unless the name says otherwise.
 */

/** Road geometry - TPDM Vol 2 Ch 3. */
export const ROAD = {
  /** Standard 2-lane carriageway total width. TPDM V2 Diagram 3.3.9.2. */
  CARRIAGEWAY_WIDTH_M: 7.3,
  /** Per-lane width. TPDM V2 Diagram 3.3.9.2 (7.3 / 2). */
  LANE_WIDTH_M: 3.65,
  /** 3-lane incl. climbing lane: 3.2 + 3.4 + 3.4. TPDM V2 3.3.8.4. */
  THREE_LANE_WIDTHS_M: [3.2, 3.4, 3.4] as const,

  /** Normal crossfall (camber). TPDM V2 3.3.3.2. Geometry, not a texture. */
  CROSSFALL: 0.025,
  /** Urban superelevation ceiling. TPDM V2 3.3.3.4. */
  MAX_SUPERELEVATION: 0.05,

  /** Absolute maximum gradient, general roads. TPDM V2 Table 3.3.6.1. */
  MAX_GRADIENT: 0.1,
  /** Desirable maximum gradient, general roads. TPDM V2 Table 3.3.6.1. */
  DESIRABLE_MAX_GRADIENT: 0.05,
  /** Minimum gradient for drainage of a kerbed road. TPDM V2 3.3.6.3. */
  MIN_GRADIENT: 0.0067,

  /** Horizontal curve radii at 50 km/h, R1..R4. TPDM V2 Table 3.3.3.1. */
  MIN_RADIUS_50_M: { R1: 44, R2: 63, R3: 88, R4: 125 } as const,

  /**
   * Vertical curve K values at 50 km/h. TPDM V2 Table 3.3.7.1 / 3.3.7.2.
   * Curve length L = K * A, where A is the algebraic grade change in percent.
   * This is how elevation gets smoothed - NOT an arbitrary kernel.
   */
  K_CREST: 10,
  K_CREST_ABSOLUTE: 6.5,
  K_SAG: 13,
  K_SAG_ABSOLUTE: 9,

  /** Stopping sight distance at 50 km/h. TPDM V2 Table 3.3.5.1. */
  SIGHT_DISTANCE_DESIRABLE_M: 70,
  SIGHT_DISTANCE_ABSOLUTE_M: 50,

  /** Driver eye height range. TPDM V2 3.3.5.2. */
  EYE_HEIGHT_MIN_M: 1.05,
  EYE_HEIGHT_MAX_M: 2.0,
} as const;

/** Design vehicle - TPDM Vol 2 Ch 2. */
export const VEHICLE = {
  /** Design private car. TPDM V2 Table 2.2.2.1. */
  LENGTH_M: 4.6,
  WIDTH_M: 1.7,
  HEIGHT_M: 1.5,

  /** Honda Civic wheelbase. TPDM V2 Table 2.2.2.2 (range 2.60-2.72). */
  WHEELBASE_M: 2.6,

  /** Honda Civic kerb-to-kerb turning circle diameter, TPDM V2 Table 2.2.2.2. */
  TURNING_CIRCLE_DIAMETER_M: 10.8,
} as const;

/** Derived vehicle parameters. Kept as functions of the cited values above. */
export const VEHICLE_DERIVED = {
  /** 10.8 / 2 = 5.4 m. */
  TURNING_RADIUS_M: VEHICLE.TURNING_CIRCLE_DIAMETER_M / 2,
  /** atan(wheelbase / turning radius) = atan(2.6 / 5.4) ~= 25.7 deg. */
  MAX_STEER_RAD: Math.atan(
    VEHICLE.WHEELBASE_M / (VEHICLE.TURNING_CIRCLE_DIAMETER_M / 2),
  ),
} as const;

/**
 * Visual/private-car geometry. These are deliberately separate from the TPDM
 * envelope above: TPDM's 1.7 m includes the tyres, so it is not body width.
 */
export const CAR_GEOMETRY = {
  BODY_WIDTH_M: 1.48,
  TRACK_M: 1.44,
  WHEEL_WIDTH_M: 0.2,
  WHEEL_RADIUS_M: 0.31,
  FLOOR_HEIGHT_M: 0.45,
} as const;

/** Design speed for an urban single carriageway / street. TPDM V2 Table 3.3.2.1. */
export const DESIGN_SPEED_KMH = 50;
export const DESIGN_SPEED_MS = DESIGN_SPEED_KMH / 3.6;

/**
 * Camera eye height. Sits inside the TPDM official driver eye-height range so
 * the view the player gets is the view the road was designed around.
 */
export const EYE_HEIGHT_M = 1.2;

/**
 * Road markings - TPDM Vol 3 Ch 5.
 *
 * NOTE: these are the values the generator currently builds to. Each is marked
 * with its verification state. `bl_road.py` refuses to bake any marking whose
 * dimensions are still UNVERIFIED unless run with --allow-unverified, so a
 * guess can never silently ship as "built to spec".
 */
export const MARKINGS = {
  /** RM 1101, roads below 70 km/h. */
  LANE_LINE: {
    strokeM: 1.0,
    gapM: 5.0,
    widthM: 0.1,
    verified: true,
    cite: 'TPDM V3 Table 5.4.2.1',
  },
  /** RM 1103. */
  CENTRE_LINE: {
    strokeM: 3.0,
    gapM: 5.0,
    widthM: 0.1,
    verified: true,
    cite: 'TPDM V3 Table 5.4.2.2',
  },
  /** RM 1108/1109; explicitly not used on this kerbed, lit urban road. */
  EDGE_LINE: {
    widthM: 0.1,
    applicable: false,
    verified: true,
    cite: 'TPDM V3 5.4.2.25 and 5.4.2.28',
  },
  /** RM 1011, one metre before the nearside primary signal. */
  STOP_LINE: {
    widthM: 0.2,
    beforePrimaryM: 1,
    verified: true,
    cite: 'TPDM V3 RM 1011 diagram and 5.6.3.3',
  },
  /** Direction arrows on roads at or below 50 km/h. */
  ARROW: {
    heightM: 4,
    approachOffsetsM: [15, 30, 45] as const,
    verified: true,
    cite: 'TPDM V3 Table 5.6.3.1',
  },
  /** RM 1038 yellow box junction. */
  BOX_JUNCTION: {
    boundaryWidthM: 0.3,
    diagonalWidthM: 0.2,
    diagonalSpacingM: 2,
    verified: true,
    cite: 'TPDM V3 Appendix 2, RM 1038',
  },
  /** RM 1040/1041 double-yellow kerb-side prohibition line. */
  DOUBLE_YELLOW: {
    lineWidthM: 0.1,
    lineGapM: 0.1,
    kerbInsetM: 0.2,
    verified: false,
    cite: 'Blocked: obtain the raster Traffic Signs and Road Markings diagrams for RM 1040/1041',
  },

  /**
   * Marking paint colour. Deliberately NOT pure white: under ACES tone mapping
   * #ffffff clips and the line loses its edge. TPDM specifies white paint; this
   * is the render-side representation of it.
   */
  COLOUR: 0xd8d8d0,
} as const;

/** Street furniture - HyD Standard Drawings. Dimensions to be filled from the PDFs. */
export const FURNITURE = {
  /** HyD Standard Drawings Section 1: Roads. STATE: unverified. */
  KERB: {
    faceHeightM: 0.15,
    widthM: 0.125,
    verified: false,
    cite: 'HyD H1118A verifies the 125 mm Type K1 urban kerb width; installed face height remains a named blocker',
  },
  /** HyD Standard Drawings Section 2: Street Furniture, Type 1-14. STATE: unverified. */
  RAILING: {
    heightM: 1.0,
    postSpacingM: 1.5,
    maxInfillGapM: 0.11,
    verified: true,
    cite: 'HyD H2130H (1,000 mm height, panels <=1,500 mm, infill <=110 mm)',
  },
  /** HyD Lighting Division standard column + 4/5/8/10/12 M bracket. STATE: unverified. */
  LAMP_POST: {
    columnHeightM: 10,
    bracketLengthM: 2,
    tiltDeg: 5,
    verified: true,
    cite: 'HyD HLDSDGE01-CL0002J (10 m column; 2 m and 5 degree bracket options); TPDM V6 10.5.3.1 places street lights in SF&GZ',
  },
  /** Minimum urban footway width. */
  FOOTWAY: {
    widthM: 3.0,
    verified: true,
    cite: 'TPDM V2 3.11.8.1',
  },
} as const;

export const FOOTBRIDGE = {
  clearanceM: 5.1,
  maintainedClearanceM: 5.0,
  maxDesirableClearanceM: 5.5,
  verified: true,
  cite: 'TPDM V2 Table 3.5.1.1 and 3.7.7.15',
} as const;

export const SIGNAL = {
  aspectDiameterM: 0.3,
  pedestrianMountMinM: 1.85,
  pedestrianMountMaxM: 2.0,
  verified: true,
  cite: 'TPDM V4 3.2.4 and 3.2.4.1',
} as const;

/**
 * Everything in this project that is still a guess, in one list.
 * `npm run build` prints it. It should shrink to zero by W4.
 */
export function unverifiedSpecs(): string[] {
  const out: string[] = [];
  for (const [k, v] of Object.entries(MARKINGS)) {
    if (typeof v === 'object' && v !== null && 'verified' in v && !v.verified) {
      out.push(`MARKINGS.${k} — ${'cite' in v ? String(v.cite) : 'citation missing'}`);
    }
  }
  for (const [k, v] of Object.entries(FURNITURE)) {
    if (!v.verified) out.push(`FURNITURE.${k} — ${v.cite}`);
  }
  return out;
}
